/***********************************************
ICEMEDX - Developed By ICEMEDX, Inc.
Copyright 2021 ICEMEDX, Inc.
***********************************************/
import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Validators, FormControl } from '@angular/forms';
import { MatTableDataSource } from '@angular/material';

import {
  IcecontrolSettings,
  iceColumnDefinition,
  IcemattableSettings,
  IcepanelgroupSettings,
  IcepanelsectionSettings,
  IceexpansionpanelSettings,
  IcepanelcontainerRowSettings,
  IcepanelcontainerSettings,
  IcecontrolPermission,
  SECTION_CONTROL_ALIGNMENT,
  IcepanelSettings
} from 'projects/icemedx-lib/src/components/models/icesettings';

import { IceEntity } from 'projects/icemedx-lib/src/models/core/IceEntity';
import { provider } from 'projects/icemedx-lib/src/models/domain/provider';
import { entity } from 'projects/icemedx-lib/src/models/common/entity';
import { contact } from 'projects/icemedx-lib/src/models/common/contact';
import { address } from '../../../../icemedx-lib/src/models/common/address';

import { AppService } from '../app.service';
import { IceNavService } from '../../../../icemedx-lib/src/components/ice-nav/ice-nav.service';
import { LoginService } from '../../../../icemedx-lib/src/login/login.service';
import { DomainService } from 'projects/icemedx-lib/src/services/domain/domain.service';
import { VendorService } from '../../../../icemedx-lib/src/services/vendor/vendor.service';
import { ProviderService } from '../../../../icemedx-lib/src/services/provider/provider.service';
import { PharmacyService } from '../../../../icemedx-lib/src/services/pharmacy/pharmacy.service';
import { PayerService } from '../../../../icemedx-lib/src/services/payer/payer.service';
import { IceTypes } from 'projects/icemedx-lib/src/models/icetypes';
import { userTypes } from 'projects/icemedx-lib/src/login/login.config';

import { DomainProfile } from '../domainprofile';
import { MedicalcodesService } from '../../../../icemedx-lib/src/services/medicalcodes/medicalcodes.service';
import { basecode } from '../../../../icemedx-lib/src/models/ehr/subs/basecode';
import { Iceutility } from '../../../../icemedx-lib/src/services/iceutility';
import { OrderService } from '../../../../icemedx-lib/src/services/orders/order.service';
import { MessageService } from '../../../../icemedx-lib/src/services/message/message.service';
import { connectionparameters } from '../../../../icemedx-lib/src/models/messages/connectionparameters';

@Component({
  selector: 'app-provider',
  templateUrl: './provider.component.html',
  styleUrls: ['./provider.component.scss'],
  providers: [AppService, IceNavService]
})
export class ProviderComponent extends DomainProfile implements OnInit {
  // Data Members
  providerObj: provider = null;

  profileViewSettings: IcepanelcontainerSettings = null;
  btnSaveProfile: IcecontrolSettings = new IcecontrolSettings("SaveButton", { text: "Save Profile", class: [], glyphClass: "fa-plus-circle" }, null, null);
  btnProviderHIEUpdateDisable: IcecontrolSettings = new IcecontrolSettings("ProviderUpdateDisableButton", { label: "Send HIE Provider Updates", class: [] }, null, null).setDisabled(true);

  constructor(appSvc: AppService,
    domainSvc: DomainService,
    vendorSvc: VendorService,
    proSvc: ProviderService,
    pharmSvc: PharmacyService,
    payerSvc: PayerService, 
    medCodeSvc: MedicalcodesService,
    messageSvc: MessageService,
    loginSvc: LoginService,
    navSvc: IceNavService,
    router: Router) {
    super(appSvc, domainSvc, vendorSvc, proSvc, pharmSvc, payerSvc, medCodeSvc, messageSvc, loginSvc, navSvc, router);

    appSvc.defaultMenus();
    appSvc.addBreadcrumb("Provider Profile", {}, true, "");

    domainSvc.getProvider(appSvc.getCurrentAccount()).subscribe(
      pro => {
        this.profileViewSettings = this.viewSetup(pro);
      },
      error => {
      });
  }

  ngOnInit() {
    this.getAppService().refreshMenus();
  }

  viewSetup(dat: IceEntity): IcepanelcontainerSettings {
    let setup: IcepanelcontainerSettings = null;
    try {
      let feaAccess = this.getAppService().getPermission(userTypes.USER_FEATURES.ufSetup).default_access;
      this.btnSaveProfile.permissions = new IcecontrolPermission(userTypes.USER_FEATURES.ufSetup, feaAccess == userTypes.USER_FEATURE_ACCESS.ufaNone ? userTypes.USER_FEATURE_ACCESS.ufaReadOnly : feaAccess);

      if (feaAccess == userTypes.USER_FEATURE_ACCESS.ufaNone) {
        this.getDomainService().logMessage(IceTypes.LOGTYPE.ltError, "You do not have access to view this data.");
        return null;
      }
      if (dat == null || dat["length"] == 0) {
        return null;
      }

      this.providerObj = <provider>dat[0];

      //////////////////////
      // Name
      if (this.providerObj.COUNTRIES == null || this.providerObj.COUNTRIES.length == 0) {
        this.providerObj.COUNTRIES = [new basecode("COUNTRY")];
      }
      if (this.providerObj.CURRENCIES == null || this.providerObj.CURRENCIES.length == 0) {
        this.providerObj.CURRENCIES = [new basecode("CURRENCY")];
      }

      let cntryCode: basecode = this.providerObj.COUNTRIES[0];
      let curcyCode: basecode = this.providerObj.CURRENCIES[0];
      let nameControls = super.viewName(this.providerObj.trans_account, this.providerObj.ENTS, this.providerObj.entity_key, true, this.providerObj.taxonomy_code, cntryCode, curcyCode);
      let nameSections: IcepanelsectionSettings[] = [
        new IcepanelsectionSettings("NameViewSection", { class: ["yalla"], styles: { width: "100%" } }, null, nameControls, null)
      ];

      //////////////////////
      // Contact Info
      let contactTables = super.viewContact(this.providerObj.CONS);
      let contactSections = this.viewContactSection(contactTables, true);

      //////////////////////
      // Address Info
      let addressTables = this.viewAddress(this.providerObj.ADS);
      let addressSections = this.viewAddressSection(addressTables, true);

      //////////////////////
      // Identification Info
      let idCodeList = [
        "24", "46", "FI", "BI", "XV", "XX", "ZZ",
        "34", "AC", "0B", "1G", "EI", "G2", "LU", "EO", "1C", "1D",
        "BO", "C1", "DH", "1E", "1J", "SPI", "ORGID"
      ];
      let idTables = super.viewIdentification(this.providerObj.IDS, idCodeList);
      let idSections = this.viewIdentificationSection(idTables, idCodeList, true);
      idSections.push(this.viewSureScriptsIdentificationSection(true));

      let ssControls = this.getSurescriptControls();
      let actStart: IcecontrolSettings = ssControls["ActiveStartTime"];
      if (actStart != null) actStart.getFormControl().setValue(Iceutility.ConvertFromDotNetServerTime(this.providerObj["activestarttime"]));

      let actEnd = ssControls["ActiveEndTime"];
      if (actEnd != null) actEnd.getFormControl().setValue(Iceutility.ConvertFromDotNetServerTime(this.providerObj["activeendtime"]));

      let updatedisable = ssControls["UpdateDisable"];
      if (updatedisable != null) updatedisable.getFormControl().setValue(this.providerObj["updatedisable"] == null ? "" : this.providerObj["addupdate"]);

      let svclvlCtrl = ssControls["ServiceLevel"];
      if (svclvlCtrl != null) {
        let svclevels = [];
        for (let ilvl = 0; ilvl < this.providerObj.SVCLEVELS.length; ilvl++) {
          let lvl = this.providerObj.SVCLEVELS[ilvl];
          svclevels.push({
            name: OrderService.getServiceLevel(<IceTypes.ServiceLevel>Iceutility.toLong(lvl["service_level"])),
            value: <IceTypes.ServiceLevel>Iceutility.toLong(lvl["service_level"])
          });
        }
        try {
          svclvlCtrl.getFormControl().setValue(JSON.stringify(svclevels));
        }
        catch (e) {
          svclvlCtrl.getFormControl().setValue("");
          console.log(e);
        }
      }

      //////////////////////
      // Subscriptions
      let subscriptionTables = this.viewSubscription([]);
      let subscriptionSections = this.viewSubscriptionSection(subscriptionTables, this.providerObj.COUNTRIES, this.providerObj.CURRENCIES, true);

      /////////////////////////
      // Network Connections
      let networkConnSections = this.viewNewOutboundNetworkConnections();
      let outboundNetworkConnectionTables = this.viewOutboundNetworkConnections(this.providerObj.CONNPARAMS, this.providerObj.TYPEREFS);
      let outboundGroups: IcepanelgroupSettings[] = [
        new IcepanelgroupSettings(null, outboundNetworkConnectionTables)
      ];

      /////////////////////////
      // Providers
      let proTables = super.viewProviders();
      let proSections = this.viewProvidersSection(proTables, true);

      if (location.hostname.toLowerCase().indexOf('dev.networknowehr.com') >= 0) {
        this.getDomainService().getSureScriptsConnectivityStatus().subscribe(
          data => {
          },
          error => {
            console.log(error);
          });
      }

      networkConnSections.push(new IcepanelsectionSettings("OutboundNetworkConnectionViewSection", { class: [""], styles: { width: "100%" } }, outboundGroups, [], null));
      networkConnSections = networkConnSections.concat(this.viewEditOutboundNetworkConnections());

      this.btnProviderHIEUpdateDisable.setDisabled(!this.getProviderHIEUpdateDisable());

      let inboundNetworkConnectionTables = this.viewInboundNetworkConnections(this.providerObj.INCONNPARAMS, this.providerObj.TYPES);
      let inboundGroups: IcepanelgroupSettings[] = [
        new IcepanelgroupSettings(null, inboundNetworkConnectionTables)
      ];
      networkConnSections.push(new IcepanelsectionSettings("InboundNetworkConnectionViewSection", { class: ["yalla"], styles: { width: "100%" } }, inboundGroups, [], null));
      networkConnSections = networkConnSections.concat(this.viewEditInboundNetworkConnections());

      let networkInfoTables = this.viewNetworkInformation(<IceEntity>this.providerObj, []/*this.providerObj.SVCLEVELS*/);
      networkConnSections = networkConnSections.concat(this.viewNetworkInformationSection(networkInfoTables, true));

      let namefrm: IcepanelSettings = new IceexpansionpanelSettings("NameView", "", "Name", "", nameSections, null, null, null)
        .enableForm();
      let nameFrmGrp = namefrm.getForm();
      let panels: IcepanelSettings[] = [
        namefrm,
        new IceexpansionpanelSettings("ContactView", "", "Contact Information", "", contactSections, null, null, null)
          .enableForm(nameFrmGrp),
        new IceexpansionpanelSettings("AddressView", "", "Address Information", "", addressSections, null, null, null)
          .enableForm(nameFrmGrp),
        new IceexpansionpanelSettings("IdentificationView", "", "Identification Information", "", idSections, null, null, null)
          .enableForm(nameFrmGrp),
        new IceexpansionpanelSettings("SubscriptionView", "", "Subscription(s) and Billing History", "", subscriptionSections, null, null, null)
          .enableForm(nameFrmGrp),
        new IceexpansionpanelSettings("NetworkConnectionView", "", "Network Connections", "", networkConnSections, null, null, null)
          .enableForm(nameFrmGrp),
      ];

      panels.push(new IceexpansionpanelSettings("ProvidersView", "", "List of Providers", "", proSections, null, null, null)
        .enableForm(nameFrmGrp));

      let panelrows: IcepanelcontainerRowSettings[] = [new IcepanelcontainerRowSettings(panels)];
      setup = new IcepanelcontainerSettings("ContactViewContainer", panelrows);
    }
    catch (e) {
      console.log(e);
    }
    return setup;
  }

  // Events
  onControlChange(event) {
    super.onControlChange(event);
  }

  onButtonClick(event) {
    this.onDefaultButtonClick(event, this.providerObj.CONNPARAMS, this.providerObj.TYPEREFS, this.editInboundRow);
  }

  public onRowClick(event) {
    super.onRowClick(event);
  }

  public onAddRowButtonClick(event) {
    this.onDefaultAddRowButtonClick(event,
      this.providerObj.CONS,
      this.providerObj.ADS,
      this.providerObj.IDS,
      this.providerObj.CONNPARAMS,
      this.providerObj.TYPEREFS,
      this.providerObj.INCONNPARAMS,
      this.providerObj.TYPES);
  }

  onRowActionClick(event) {
    this.onDefaultRowActionClick(event);
    let entry = event["rowData"];
    if (entry == null) {
      // To do log error
      return;
    }
    if (IceTypes.recordQuickActionRemove.value == event.actionItem.value) {
      debugger;
      if (Iceutility.yesnomodal("Would you like to remove this connection?")) {
        let conn: connectionparameters = <connectionparameters>entry;
        let fConns = this.providerObj.CONNPARAMS.filter(c => c.connection_parameters_key == conn.connection_parameters_key);
        if (fConns.length > 0) {
          fConns[0].offline_record_status = IceTypes.offlineRecStatus.offlineDelete;
        }

        let fTrefs = this.providerObj.TYPEREFS.filter(t => t.connection_parameters_key == conn.connection_parameters_key);
        if (fTrefs.length > 0) {
          fTrefs[0].offline_record_status = IceTypes.offlineRecStatus.offlineDelete;
        }
      }
    }
  }

  onSaveProfile(event) {
    let taxonomyCtrl = this.getTaxonomyCodeControl();
    if (taxonomyCtrl != null) {
      let txFrmCtrl = taxonomyCtrl.getFormControl();
      if (txFrmCtrl && txFrmCtrl.dirty) {
        this.providerObj.taxonomy_code = txFrmCtrl.value;
      }
    }
    if (this.providerObj.taxonomy_code.length == 0) {
      this.getDomainService().logMessage(IceTypes.LOGTYPE.ltError, "The Taxonomy code field is required.");
      return;
    }

    if (false == this.updateName(this.providerObj.COUNTRIES, this.providerObj.CURRENCIES)) {
      // To do error log
      return;
    }

    let ssControls = this.getSurescriptControls();
    let actStart: IcecontrolSettings = ssControls["ActiveStartTime"];
    if (actStart != null) this.providerObj["activestarttime"] = Iceutility.convertToUTCEx(actStart.getFormControl().value);

    let actEnd = ssControls["ActiveEndTime"];
    if (actEnd != null) this.providerObj["activeendtime"] = Iceutility.convertToUTCEx(actEnd.getFormControl().value);

    let updateDisable = ssControls["UpdateDisable"];
    if (updateDisable != null) this.providerObj["updatedisable"] = updateDisable.getFormControl().value;

    let serviceLevelCtrl = ssControls["ServiceLevel"];
    if (serviceLevelCtrl != null) {
      let svcLevels = serviceLevelCtrl.getFormControl().value;
      try {
        let svcLvlObject = JSON.parse(svcLevels);
        if (svcLvlObject != null) {
          this.providerObj.SVCLEVELS = [];
          for (let isvc = 0; isvc < svcLvlObject.length; isvc++) {
            let svc = svcLvlObject[isvc];
            if (svc == null) continue;
            this.providerObj.SVCLEVELS.push(Iceutility.toLong(svc.value));
          }
        }
      }
      catch (e) {
        console.log(e);
      }
    }

    this.updateContact(this.providerObj.CONS);
    this.updateAddress(this.providerObj.ADS);

    if (Iceutility.isdefined(this.providerObj.COUNTRIES)
      && this.providerObj.COUNTRIES.length == 0
      && Iceutility.isdefined(this.providerObj.ADS)) {

      this.providerObj.COUNTRIES = [new basecode("COUNTRY")];
      this.providerObj.COUNTRIES[0].code = this.providerObj.ADS[0].country_id;
    }

    this.updateIdentification(this.providerObj.IDS);
    this.updateOutboundDomainConnection(this.providerObj.CONNPARAMS, this.providerObj.TYPEREFS);
    this.updateInboundDomainConnection(this.providerObj.INCONNPARAMS, this.providerObj.TYPES);
    let pros = this.updateProviders(),
      accountType = this.getAppService().getCurrentAccountType();

    if (IceTypes.ACCOUNT_TYPE.actFacility === accountType) {
      let filteredProviders = pros.filter(p => p.updatedisable === true);
      if (filteredProviders.length > 0) {
        if (this.providerObj.CONS.filter(c => c.communication_number_qualifier === "FX").length === 0) {
          this.messageSvc.logMessage(IceTypes.LOGTYPE.ltError, "Fax number is required");
          window.scrollTo(0, 0);
          return false;
        }
      }
    }

    this.getProviderService().updateProvider(this.providerObj, accountType, this.getProviderHIEUpdateDisable(), pros).subscribe(
      data => {
        this.getRouter().navigate([this.getNavService().buildRoute("dashboard", false)]);
      },
      error => {
        console.log(error);
    });
  }
}
